import { useEffect, useMemo, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { Dropdown } from '@/components/ui/Dropdown'
import { LanguageFlag } from '@/components/ui/LanguageFlag'
import { useLanguages } from '@/hooks/useLanguages'
import { useListboxNav } from '@/hooks/useListboxNav'
import { cn } from '@/utils/cn'
import type { Language } from '@/types/language'

export function LanguageDropdown() {
  const { languages, selectedLanguageId, setSelectedLanguageId, enroll } = useLanguages()
  const [open, setOpen] = useState(false)
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([])

  const enrolled = languages.filter((l) => l.enrolled)
  const discover = languages.filter((l) => !l.enrolled)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const options = useMemo(() => [...enrolled, ...discover], [languages])
  const activeId = enrolled.find((l) => l.id === selectedLanguageId)?.id ?? enrolled[0]?.id ?? null
  const current = enrolled.find((l) => l.id === activeId) ?? null

  function handleSelect(index: number) {
    const opt = options[index]
    if (!opt) return
    setOpen(false)
    const goTo = () => {
      setSelectedLanguageId(opt.id)
      router.visit(`/languages/${opt.id}`)
    }
    if (opt.enrolled) goTo()
    else void enroll(opt.id).then(goTo)
  }

  const { activeIndex, setActiveIndex, handleKeyDown } = useListboxNav(options.length, {
    onSelect: handleSelect,
    onClose: () => setOpen(false),
    open,
  })

  useEffect(() => {
    if (open) optionRefs.current[activeIndex]?.focus()
  }, [open, activeIndex])

  function renderOption(lang: Language, index: number) {
    const selected = lang.id === activeId
    return (
      <button
        key={lang.id}
        id={`lang-option-${lang.id}`}
        ref={(el) => {
          optionRefs.current[index] = el
        }}
        type="button"
        role="option"
        aria-selected={selected}
        tabIndex={-1}
        onClick={() => handleSelect(index)}
        onMouseEnter={() => setActiveIndex(index)}
        className={cn(
          'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors duration-150 focus:outline-none',
          index === activeIndex && 'bg-surface-bg/60',
          lang.enrolled ? 'text-body' : 'text-muted',
          'hover:text-accent',
        )}
      >
        <LanguageFlag isoCode={lang.iso_code} size="s" />
        <span className="flex-1">{lang.name}</span>
        {selected && (
          <span aria-hidden="true" className="text-accent text-xs">
            ✓
          </span>
        )}
      </button>
    )
  }

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      align="right"
      trigger={
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 h-11 px-2 rounded-md text-nav-toggle hover:text-nav-toggle-hover transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
          aria-label="Select language"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {current ? <LanguageFlag isoCode={current.iso_code} size="m" /> : <GlobeIcon />}
          {current && <span className="hidden sm:inline font-serif text-sm">{current.name}</span>}
          <ChevronIcon open={open} />
        </button>
      }
    >
      <div
        role="listbox"
        aria-label="Languages"
        aria-activedescendant={
          options[activeIndex] ? `lang-option-${options[activeIndex].id}` : undefined
        }
        onKeyDown={handleKeyDown}
        tabIndex={-1}
        className="py-2 max-h-[70vh] overflow-y-auto outline-none"
      >
        {enrolled.length > 0 && <GroupLabel>Enrolled</GroupLabel>}
        {enrolled.map((lang, i) => renderOption(lang, i))}
        {discover.length > 0 && (
          <>
            <div role="presentation" className="border-t border-line-soft my-2 mx-4" />
            <GroupLabel>Discover</GroupLabel>
          </>
        )}
        {discover.map((lang, i) => renderOption(lang, enrolled.length + i))}
      </div>
    </Dropdown>
  )
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="presentation"
      className="px-4 pt-2 pb-1 font-mono text-[10px] uppercase tracking-[0.28em] text-muted"
    >
      {children}
    </p>
  )
}

function GlobeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  )
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
      aria-hidden="true"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}
